/**
 * AmplitudeServerEditor - Configuration editor for Amplitude Server-Side
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import type { AmplitudeServerConfig, PlatformConfig } from '../../types/analytics.types';

interface AmplitudeServerEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const AmplitudeServerEditor: React.FC<AmplitudeServerEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const ampConfig = config as AmplitudeServerConfig;
  const [localConfig, setLocalConfig] = useState<AmplitudeServerConfig>(ampConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(ampConfig);
    setLocalName(name || '');
  }, [ampConfig, name]);

  const handleChange = useCallback(
    (field: keyof AmplitudeServerConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<'US' | 'EU'>) => {
      handleChange('serverZone', event.target.value);
    },
    [handleChange]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Amplitude Server-Side sends revenue events directly from the server for accurate purchase tracking.
        This supplements the client-side Amplitude tracking.
      </Alert>

      <TextField
        label="API Key"
        value={localConfig.apiKey || ''}
        onChange={(e) => handleChange('apiKey', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        type="password"
        sx={{ mb: 2 }}
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        helperText={
          <>
            Same API Key as your client-side Amplitude. Find it in{' '}
            <Link
              href="https://amplitude.com/docs/apis/keys-and-tokens"
              target="_blank"
              rel="noopener"
            >
              Amplitude Settings → Projects → API Key
            </Link>
          </>
        }
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Server Zone</InputLabel>
        <Select
          value={localConfig.serverZone || 'US'}
          onChange={handleSelectChange}
          disabled={disabled || readOnly}
          label="Server Zone"
        >
          <MenuItem value="US">US (api2.amplitude.com)</MenuItem>
          <MenuItem value="EU">EU (api.eu.amplitude.com)</MenuItem>
        </Select>
      </FormControl>

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.apiKey}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
